import React, { useState, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Table, Image, Modal, Form } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function SellerProductDetail() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const location = useLocation();

    const product = location.state?.product || null;
    const [modalImage, setModalImage] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    if (!product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <p className="text-muted fw-semibold">{t("No product data available.")}</p>
                    <Button variant="primary" onClick={() => navigate(-1)}>{t("Go Back")}</Button>
                </div>
            </PageLayout>
        );
    }

    const handleApprove = async () => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");
            await axios.put(`${process.env.REACT_APP_API_URL}/admin/seller-product-update/${product.id}`, product, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate(-1);
        } catch (err) {
            console.error("Failed to approve product:", err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");
            await axios.put(`${process.env.REACT_APP_API_URL}/admin/seller-product-reject/${product.id}`, { reason: rejectReason }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowRejectModal(false);
            navigate(-1);
        } catch (err) {
            console.error("Failed to reject product:", err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <Row className="g-4">
                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <h4 className="mb-0 fw-bold text-dark">{product.title}</h4>
                                    <span className="badge bg-light text-secondary border">#{product.product_code}</span>
                                </div>
                                <span className="text-muted small">Created At: {new Date(product.created_at).toLocaleString()}</span>
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="success" size="sm" onClick={handleApprove} disabled={submitting}>
                                    {t("Approve Product")}
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => setShowRejectModal(true)} disabled={submitting}>
                                    {t("Reject")}
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>{t("Back")}</Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={8}>
                    <Card className="mb-4 border-0 shadow-sm rounded-3">
                        <Card.Header className="bg-white py-3">
                            <h6 className="mb-0 fw-bold">{t("Product Overview")}</h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Row className="g-4 mb-4">
                                <Col md={5}>
                                    <div className="mb-3">
                                        <span className="d-block text-muted small fw-semibold mb-2">{t("Primary Image")}</span>
                                        {product.images?.[0]?.image ? (
                                            <div className="p-1 border rounded bg-light text-center cursor-pointer" onClick={() => setModalImage(resolveImageUrl(product.images[0].image))}>
                                                <Image src={resolveImageUrl(product.images[0].image)} fluid rounded style={{ maxHeight: "200px", objectFit: "contain" }} />
                                            </div>
                                        ) : (
                                            <div className="p-3 border rounded bg-light text-muted small">{t("No Image")}</div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={7}>
                                    <div className="mb-3">
                                        <h6 className="fw-bold">{t("Short Description")}</h6>
                                        <div className="p-3 bg-light border rounded small" dangerouslySetInnerHTML={{ __html: product.short_description || "N/A" }} />
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Badge bg="light" className="text-dark border">Condition: <strong>{product.product_condition}</strong></Badge>
                                        <Badge bg="light" className="text-dark border">Style: <strong>{product.selling_style}</strong></Badge>
                                    </div>
                                </Col>
                            </Row>

                            <div className="p-3 bg-light rounded border mb-4">
                                <Row className="text-center g-2">
                                    <Col sm={3}><span>{t("Quantity")}</span><br /><strong>{product.qty}</strong></Col>
                                    <Col sm={3}><span>{t("Brand")}</span><br /><strong>{product.brand?.name || "N/A"}</strong></Col>
                                    <Col sm={3}><span>{t("Category")}</span><br /><strong>{product.category?.name || "N/A"}</strong></Col>
                                    <Col sm={3}><span>{t("Store")}</span><br /><strong>{product.store?.name || "N/A"}</strong></Col>
                                </Row>
                            </div>

                            <div className="mb-4">
                                <h6 className="fw-bold">{t("Full Description")}</h6>
                                <div className="p-3 bg-light border rounded small" dangerouslySetInnerHTML={{ __html: product.description || "N/A" }} />
                            </div>

                            {Array.isArray(product.images) && product.images.length > 0 && (
                                <div>
                                    <h6 className="fw-bold mb-2">{t("Gallery Images")}</h6>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {product.images.map((imgObj, idx) => (
                                            <div key={idx} className="border rounded p-1 bg-light cursor-pointer" onClick={() => setModalImage(resolveImageUrl(imgObj.image))}>
                                                <Image src={resolveImageUrl(imgObj.image)} rounded style={{ width: "65px", height: "65px", objectFit: "cover" }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={4}>
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="bg-white py-3"><h6 className="mb-0 fw-bold">{t("Pricing Details")}</h6></Card.Header>
                        <Card.Body>
                            <div className="mb-3 p-2 bg-light rounded text-center">
                                <span className="text-muted d-block small">{t("Buying Price")}</span>
                                <strong className="text-dark fs-6">{product.buying_price}</strong>
                            </div>
                            <div className="mb-3 p-2 bg-light rounded text-center">
                                <span className="text-muted d-block small">{t("Sell Price ($)")}</span>
                                <strong className="text-primary fs-6">${product.sell_price_dollar}</strong>
                            </div>
                            <div className="p-2 bg-light rounded text-center">
                                <span className="text-muted d-block small">{t("Shipping Charges ($)")}</span>
                                <strong className="text-dark fs-6">${product.shipping_charges_dollar}</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Reject Reason Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>{t("Reject Product Submission")}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>{t("Reason for Rejection")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={t("Enter reason...")}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>{t("Cancel")}</Button>
                    <Button variant="danger" onClick={handleReject} disabled={submitting}>{t("Confirm Rejection")}</Button>
                </Modal.Footer>
            </Modal>

            {/* Image Zoom Modal */}
            <Modal show={!!modalImage} onHide={() => setModalImage(null)} centered size="lg">
                <Modal.Body className="text-center p-0 bg-dark position-relative">
                    <Button variant="light" size="sm" className="position-absolute top-0 end-0 m-3 rounded-circle" onClick={() => setModalImage(null)} style={{ zIndex: 10 }}>&times;</Button>
                    <img src={modalImage} alt="Zoomed" className="img-fluid" style={{ maxHeight: "80vh", objectFit: "contain" }} />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}